import { Download, ExternalLink, FileDown, Link2, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  TriageActionDialog,
  type TriageAction,
} from "@/components/experience/triage-action-dialog";
import {
  CreateIssueDialog,
  LinkExistingDialog,
} from "@/components/workflow/create-issue-dialog";
import { BulkCreateIssuesDialog } from "@/components/workflow/bulk-create-issues-dialog";
import { HtmlReportShadow } from "./html-report-shadow";

interface WorkspaceDialogsProps {
  projectId: string;
  scenarioName?: string;
  actionDialog: {
    action: TriageAction;
    ids: string[];
    subject?: string;
  } | null;
  onCloseActionDialog: () => void;
  createIssueCanonicalId: string | null;
  onCloseCreateIssue: () => void;
  linkExistingCanonicalId: string | null;
  onCloseLinkExisting: () => void;
  bulkIssuesOpen: boolean;
  onCloseBulkIssues: () => void;
  bulkCanonicalIds: string[];
  reportOpen: boolean;
  onCloseReport: () => void;
  htmlReportContent?: string | null;
  isSharing: boolean;
  shareInfo: { enabled: boolean; shareUrl: string | null } | null;
  onSharePublicLink: () => void;
  onDownloadPdf: () => void;
  onDownloadHtml: () => void;
}

export function WorkspaceDialogs({
  projectId,
  scenarioName,
  actionDialog,
  onCloseActionDialog,
  createIssueCanonicalId,
  onCloseCreateIssue,
  linkExistingCanonicalId,
  onCloseLinkExisting,
  bulkIssuesOpen,
  onCloseBulkIssues,
  bulkCanonicalIds,
  reportOpen,
  onCloseReport,
  htmlReportContent,
  isSharing,
  shareInfo,
  onSharePublicLink,
  onDownloadPdf,
  onDownloadHtml,
}: WorkspaceDialogsProps) {
  return (
    <>
      {actionDialog && (
        <TriageActionDialog
          action={actionDialog.action}
          ids={actionDialog.ids}
          projectId={projectId}
          subject={actionDialog.subject}
          onClose={onCloseActionDialog}
        />
      )}

      {createIssueCanonicalId && (
        <CreateIssueDialog
          canonicalId={createIssueCanonicalId}
          open={!!createIssueCanonicalId}
          onOpenChange={(open) => !open && onCloseCreateIssue()}
        />
      )}

      {linkExistingCanonicalId && (
        <LinkExistingDialog
          canonicalId={linkExistingCanonicalId}
          open={!!linkExistingCanonicalId}
          onOpenChange={(open) => !open && onCloseLinkExisting()}
        />
      )}

      {bulkIssuesOpen && (
        <BulkCreateIssuesDialog
          projectId={projectId}
          canonicalIds={bulkCanonicalIds}
          open={bulkIssuesOpen}
          onOpenChange={(open) => !open && onCloseBulkIssues()}
        />
      )}

      {htmlReportContent && (
        <Dialog
          open={reportOpen}
          onOpenChange={(open) => !open && onCloseReport()}
        >
          <DialogContent
            className="h-[90vh] max-w-[90vw] overflow-y-auto p-0 sm:max-w-[90vw]"
            showCloseButton={false}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-6 py-3">
              <DialogTitle>
                {scenarioName ?? "Walkthrough"} — Report
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSharePublicLink}
                  disabled={isSharing}
                >
                  <Link2 className="mr-1.5 h-3.5 w-3.5" />
                  Copy share link
                </Button>
                {shareInfo?.enabled && shareInfo?.shareUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(shareInfo.shareUrl!, "_blank")}
                  >
                    <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                    Open public
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={onDownloadPdf}>
                  <FileDown className="mr-1.5 h-3.5 w-3.5" />
                  Download PDF
                </Button>
                <Button variant="outline" size="sm" onClick={onDownloadHtml}>
                  <Download className="mr-1.5 h-3.5 w-3.5" />
                  Download HTML
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onCloseReport}
                  aria-label="Close report"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="px-6 pb-6">
              <HtmlReportShadow html={htmlReportContent} />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
